<script setup lang="ts">
/**
 * "Send me a reset link": one email, one captcha, one yellow plate; then a
 * single line saying the link is on its way. The server answers the same for
 * an address it knows and one it does not, and so does this screen.
 *
 * Owns its own busy / done / error state; the join screen only swaps it in and
 * hears `back`.
 */
import { computed, ref } from 'vue';

import { ApiError, api } from '@/net/api';
import { useI18n } from '@/i18n';
import ActionButton from '../panel/ActionButton.vue';
import AuthField from './AuthField.vue';
import AuthNotice from './AuthNotice.vue';
import CaptchaWell from './CaptchaWell.vue';

const emit = defineEmits<{ back: [] }>();
const { t } = useI18n();

const email = ref('');
const altcha = ref('');
const captcha = ref<InstanceType<typeof CaptchaWell> | null>(null);
const busy = ref(false);
const done = ref(false);
const error = ref<string | null>(null);

const canSubmit = computed(() => !busy.value && email.value.trim().length > 0 && altcha.value !== '');

async function submit(): Promise<void> {
  if (!canSubmit.value) return;
  busy.value = true;
  error.value = null;
  try {
    await api.forgotPassword(email.value.trim(), altcha.value);
    done.value = true;
  } catch (err) {
    error.value = err instanceof ApiError ? err.message : t('auth.unreachable');
    captcha.value?.rerun();
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <template v-if="done">
    <AuthNotice data-test="forgot-sent">{{ t('join.forgotSent') }}</AuthNotice>
    <ActionButton variant="primary" block size="lg" @click="emit('back')">
      {{ t('join.backToSignIn') }}
    </ActionButton>
  </template>

  <form v-else class="forgot" @submit.prevent="submit">
    <AuthField
      id="forgot-email"
      v-model="email"
      type="email"
      autocomplete="email"
      :label="t('join.email')"
      :placeholder="t('join.email')"
      :disabled="busy"
    />
    <CaptchaWell ref="captcha" v-model="altcha" />
    <AuthNotice v-if="error" tone="error">{{ error }}</AuthNotice>
    <ActionButton type="submit" variant="primary" block size="lg" :disabled="!canSubmit" :loading="busy">
      {{ busy ? t('auth.working') : t('join.forgotSend') }}
    </ActionButton>
    <ActionButton size="sm" @click="emit('back')">{{ t('join.backToSignIn') }}</ActionButton>
  </form>
</template>

<style scoped>
.forgot {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
